import type { DayOfWeek, ItemCategory, ItemImage } from "@/lib/types";
import type { SellPriceBandSnapshot } from "@/lib/hooks/useSellPriceBand";
import type { DevImageValidationPreview } from "@/lib/listing/devImageValidationPreviewCopy";
import type { DevToolboxScenarioId } from "@/lib/devToolbox/types";
import { SELLER_PHOTO_COACH_DISMISS_KEY } from "@/components/listing/SellerPhotoCoach";

export const DEV_TOOLBOX_SELL_MOCK_IMAGE_URL =
  "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&w=400&q=80";

export type { DevImageValidationPreview } from "@/lib/listing/devImageValidationPreviewCopy";

export type SellDevToolboxFormSlice = {
  title: string;
  description: string;
  defects: string;
  price: string;
  quantity: number;
  category: ItemCategory | "";
  subcategory: string;
  location: string;
  brand: string;
  size: string;
  model: string;
  pickupStartHour: string;
  pickupEndHour: string;
};

export type SellDevToolboxApplyResult = {
  formData: SellDevToolboxFormSlice;
  uploadedImages: ItemImage[];
  pickupDays: DayOfWeek[];
  initialStep: number;
  bandSnapshot: SellPriceBandSnapshot | null;
  bandError: string | null;
  imageValidationPreview: DevImageValidationPreview;
  photoCoachDismissed: boolean | null;
};

const EMPTY_FORM: SellDevToolboxFormSlice = {
  title: "",
  description: "",
  defects: "",
  price: "",
  quantity: 1,
  category: "",
  subcategory: "",
  location: "",
  brand: "",
  size: "",
  model: "",
  pickupStartHour: "9",
  pickupEndHour: "17",
};

const PARTIAL_FORM: SellDevToolboxFormSlice = {
  title: "Dev mock wooden dining table",
  description: "Light wear on one leg; great for staging sell form review.",
  defects: "",
  price: "",
  quantity: 1,
  category: "furniture",
  subcategory: "tables",
  location: "",
  brand: "",
  size: "",
  model: "",
  pickupStartHour: "9",
  pickupEndHour: "17",
};

const READY_FORM: SellDevToolboxFormSlice = {
  title: "Dev mock wooden dining table",
  description:
    "Ready-to-submit sample listing for dev toolbox. No API submit from this state.",
  defects: "Minor scratch on one corner.",
  price: "185,000",
  quantity: 1,
  category: "furniture",
  subcategory: "tables",
  location: "Lagos, Ikeja",
  brand: "",
  size: "",
  model: "",
  pickupStartHour: "10",
  pickupEndHour: "16",
};

const MOCK_BAND: SellPriceBandSnapshot = {
  min: 150_000,
  max: 250_000,
  enforcementEligible: true,
  bandPolicy: "enforce",
  sampleCount: 42,
};

function buildMockImages(count: number): ItemImage[] {
  return Array.from({ length: count }, (_, index) => ({
    id: `dev-sell-mock-image-${index + 1}`,
    itemId: "",
    url: DEV_TOOLBOX_SELL_MOCK_IMAGE_URL,
    position: index,
    isMain: index === 0,
    fileType: "image/jpeg",
  }));
}

const SELL_SCENARIO_APPLY: Partial<
  Record<DevToolboxScenarioId, SellDevToolboxApplyResult>
> = {
  "seller-listing.form-empty": {
    formData: EMPTY_FORM,
    uploadedImages: [],
    pickupDays: [],
    initialStep: 1,
    bandSnapshot: null,
    bandError: null,
    imageValidationPreview: null,
    photoCoachDismissed: null,
  },
  "seller-listing.form-partial": {
    formData: PARTIAL_FORM,
    uploadedImages: [],
    pickupDays: [],
    initialStep: 2,
    bandSnapshot: null,
    bandError: null,
    imageValidationPreview: null,
    photoCoachDismissed: null,
  },
  "seller-listing.form-ready": {
    formData: READY_FORM,
    uploadedImages: buildMockImages(3),
    pickupDays: [6],
    initialStep: 6,
    bandSnapshot: MOCK_BAND,
    bandError: null,
    imageValidationPreview: null,
    photoCoachDismissed: false,
  },
  "seller-listing.photo-coach-visible": {
    formData: { ...PARTIAL_FORM },
    uploadedImages: buildMockImages(1),
    pickupDays: [],
    initialStep: 5,
    bandSnapshot: null,
    bandError: null,
    imageValidationPreview: null,
    photoCoachDismissed: false,
  },
  "seller-listing.photo-coach-dismissed": {
    formData: { ...PARTIAL_FORM },
    uploadedImages: buildMockImages(1),
    pickupDays: [],
    initialStep: 5,
    bandSnapshot: null,
    bandError: null,
    imageValidationPreview: null,
    photoCoachDismissed: true,
  },
  "seller-listing.image-validation-warn": {
    formData: { ...PARTIAL_FORM },
    uploadedImages: buildMockImages(1),
    pickupDays: [],
    initialStep: 5,
    bandSnapshot: null,
    bandError: null,
    imageValidationPreview: "warn",
    photoCoachDismissed: null,
  },
  "seller-listing.image-validation-block": {
    formData: { ...PARTIAL_FORM },
    uploadedImages: buildMockImages(1),
    pickupDays: [],
    initialStep: 5,
    bandSnapshot: null,
    bandError: null,
    imageValidationPreview: "block",
    photoCoachDismissed: null,
  },
  "seller-listing.price-in-band": {
    formData: {
      ...READY_FORM,
      price: "185,000",
    },
    uploadedImages: buildMockImages(3),
    pickupDays: [6],
    initialStep: 4,
    bandSnapshot: MOCK_BAND,
    bandError: null,
    imageValidationPreview: null,
    photoCoachDismissed: null,
  },
  "seller-listing.price-out-of-band": {
    formData: {
      ...READY_FORM,
      price: "75,000",
    },
    uploadedImages: buildMockImages(3),
    pickupDays: [6],
    initialStep: 4,
    bandSnapshot: MOCK_BAND,
    bandError: null,
    imageValidationPreview: null,
    photoCoachDismissed: null,
  },
  "seller-listing.pricing-insight-error": {
    formData: {
      ...PARTIAL_FORM,
      price: "185,000",
    },
    uploadedImages: [],
    pickupDays: [],
    initialStep: 4,
    bandSnapshot: null,
    bandError: "Dev mock: pricing insight band lookup failed (preview only).",
    imageValidationPreview: null,
    photoCoachDismissed: null,
  },
};

export function getSellDevToolboxApplyResult(
  scenarioId: DevToolboxScenarioId | null,
): SellDevToolboxApplyResult | null {
  if (!scenarioId) return null;
  return SELL_SCENARIO_APPLY[scenarioId] ?? null;
}

export function applySellPhotoCoachDismissState(dismissed: boolean | null) {
  if (typeof window === "undefined" || dismissed === null) return;
  try {
    if (dismissed) {
      sessionStorage.setItem(SELLER_PHOTO_COACH_DISMISS_KEY, "1");
    } else {
      sessionStorage.removeItem(SELLER_PHOTO_COACH_DISMISS_KEY);
    }
  } catch {
    // Ignore storage failures.
  }
}
